import React from "react";
import { Box, Table, TableProps } from "@amzn/awsui-components-react";

import { PivotCell } from "./PivotCell";
import { PivotRowLabel } from "./PivotRowLabel";
import {
  pivotCellKey,
  type PivotColumn,
  type PivotRow as PivotRowData,
  type RowGroupPath,
} from "./stepBreakdown";
import type { StepFilterClick } from "./stepFilterTokenToggle";

export interface PivotTableProps {
  readonly columns: readonly PivotColumn[];
  readonly rows: readonly PivotRowData[];
  /**
   * Optional summary row rendered at the bottom of the table as a
   * non-expandable trailing item. Callers pass the synthesized
   * grand-total row; it renders after every `rows` entry.
   */
  readonly grandTotalRow?: PivotRowData;
  readonly expandedRowPaths: ReadonlySet<string>;
  readonly onToggleExpanded: (path: RowGroupPath) => void;
  readonly onCellClick: (click: StepFilterClick) => void;
  readonly activeStepFilter: StepFilterClick | undefined;
  readonly hiddenColumns: ReadonlySet<string>;
}

const ROW_LABEL_COLUMN_MIN_WIDTH = 340;
const DATA_COLUMN_MIN_WIDTH = 88;
// Explicit initial width for data columns. Cloudscape ignores
// `maxWidth` when `resizableColumns` is enabled, so we set a starting
// width and let users drag as needed.
const DATA_COLUMN_INITIAL_WIDTH = 112;
const EMPTY_STATE_MIN_WIDTH = 200;

const EMPTY_STATE_TEXT = "No validation steps are defined.";

function encodeRowGroupPath(path: RowGroupPath): string {
  if (path.length === 0) {
    return "grandTotal";
  }
  return path.map((t) => `${t.level}:${t.value}`).join("/");
}

function pathsEqual(a: RowGroupPath, b: RowGroupPath): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].level !== b[i].level || a[i].value !== b[i].value) {
      return false;
    }
  }
  return true;
}

function isActiveFilterForCell(
  active: StepFilterClick | undefined,
  row: PivotRowData,
  column: PivotColumn
): boolean {
  if (active === undefined) {
    return false;
  }
  return (
    active.stepName === column.stepName &&
    active.pipeline === column.pipeline &&
    pathsEqual(active.path, row.path)
  );
}

// Walk the tree and collect every row whose encoded path is in
// `expandedPaths`. Cloudscape's Table uses object identity (or
// trackBy) to match `expandedItems` back to the visible rows, so we
// need the row references from the current pivot tree, not from a
// previous render.
function collectExpandedItems(
  rows: readonly PivotRowData[],
  expandedPaths: ReadonlySet<string>,
  out: PivotRowData[]
): void {
  for (const row of rows) {
    if (expandedPaths.has(encodeRowGroupPath(row.path))) {
      out.push(row);
      collectExpandedItems(row.children, expandedPaths, out);
    }
  }
}

export const PivotTable: React.FC<PivotTableProps> = ({
  columns,
  rows,
  grandTotalRow,
  expandedRowPaths,
  onToggleExpanded,
  onCellClick,
  activeStepFilter,
  hiddenColumns,
}) => {
  const visibleColumns = React.useMemo(
    () => columns.filter((c) => !hiddenColumns.has(pivotCellKey(c))),
    [columns, hiddenColumns]
  );

  const hasColumns = visibleColumns.length > 0;

  const items = React.useMemo<readonly PivotRowData[]>(
    () => (grandTotalRow === undefined ? rows : [...rows, grandTotalRow]),
    [rows, grandTotalRow]
  );

  const expandedItems = React.useMemo<readonly PivotRowData[]>(() => {
    const out: PivotRowData[] = [];
    collectExpandedItems(rows, expandedRowPaths, out);
    return out;
  }, [rows, expandedRowPaths]);

  const columnDefinitions = React.useMemo<
    ReadonlyArray<TableProps.ColumnDefinition<PivotRowData>>
  >(() => {
    const labelColumn: TableProps.ColumnDefinition<PivotRowData> = {
      id: "row-label",
      header: "Group",
      minWidth: ROW_LABEL_COLUMN_MIN_WIDTH,
      isRowHeader: true,
      cell: (row) => <PivotRowLabel row={row} />,
    };

    if (!hasColumns) {
      return [
        labelColumn,
        {
          id: "empty-state",
          header: "Validation steps",
          minWidth: EMPTY_STATE_MIN_WIDTH,
          cell: () => (
            <Box color="text-body-secondary" fontSize="body-m" variant="span">
              <span style={{ fontStyle: "italic" }}>{EMPTY_STATE_TEXT}</span>
            </Box>
          ),
        },
      ];
    }

    const dataColumns = visibleColumns.map<
      TableProps.ColumnDefinition<PivotRowData>
    >((col) => {
      const columnKey = pivotCellKey(col);
      return {
        id: columnKey,
        header: col.headerLabel,
        width: DATA_COLUMN_INITIAL_WIDTH,
        minWidth: DATA_COLUMN_MIN_WIDTH,
        cell: (row) => {
          const cell = row.cellsByColumnKey.get(columnKey);
          if (cell === undefined) {
            return null;
          }
          return (
            <PivotCell
              cell={cell}
              row={row}
              column={col}
              onClick={onCellClick}
              isActiveFilter={isActiveFilterForCell(activeStepFilter, row, col)}
            />
          );
        },
      };
    });

    return [labelColumn, ...dataColumns];
  }, [visibleColumns, hasColumns, onCellClick, activeStepFilter]);

  return (
    <Table<PivotRowData>
      items={items}
      trackBy={(row) => encodeRowGroupPath(row.path)}
      columnDefinitions={columnDefinitions}
      variant="borderless"
      stickyHeader
      stickyColumns={{ first: 1 }}
      stripedRows
      resizableColumns
      contentDensity="compact"
      expandableRows={{
        // Hide Grand Total's children from Cloudscape's traversal —
        // otherwise it re-visits every productLine as a child of
        // Grand Total and bumps their indent one level too deep.
        getItemChildren: (row) =>
          row.level === "grandTotal" ? [] : row.children,
        isItemExpandable: (row) =>
          row.level !== "grandTotal" &&
          row.level !== "asinType" &&
          row.children.length > 0,
        expandedItems,
        onExpandableItemToggle: ({ detail }) => {
          onToggleExpanded(detail.item.path);
        },
      }}
      ariaLabels={{
        tableLabel: "Step breakdown pivot",
        expandButtonLabel: (row) => `Expand ${row.label}`,
        collapseButtonLabel: (row) => `Collapse ${row.label}`,
      }}
    />
  );
};

export default PivotTable;
